import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { JSelect } from '@atoms/JSelect';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import { useDebouncedCallback } from 'use-debounce';
import IOption from '@models/option';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import { useGetEstablishmentsSuggestions } from '@/modules/establishment/queries/useGetEstablishment';
import { JButton } from '@atoms/JButton';
import { JPhoneInput } from '@atoms/JPhoneInput';
import JTextFieldBase from '@atoms/JTextFieldBase';
import { UserRole } from '../../models/user-role';

interface Props {
  filters: ISearchFilter[];
  onApplyFilter: (filters: ISearchFilter[]) => void;
}

const FilterUsersDialogBody: React.FC<Props> = ({
  filters,
  onApplyFilter: setFilter,
}: Props) => {
  const { t } = useTranslation();

  const [dialogSearchFilter, setDialogSearchFilter] =
    useState<ISearchFilter[]>(filters);

  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');

  const [establishmentInputValue, setEstablishmentInputValue] =
    useState<string>('');

  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );
  const { data: establishments } = useGetEstablishmentsSuggestions(
    10,
    1,
    establishmentInputValue,
  );

  const appendSearchFilter = (
    field: FilterCriteriaFields,
    value: string,
    label?: string,
  ): void => {
    const existed = dialogSearchFilter?.some((item) => item.field === field);

    if (!existed)
      setDialogSearchFilter([...dialogSearchFilter, { field, value, label }]);
    if (value === '')
      setDialogSearchFilter((prevValue) =>
        prevValue.filter((item) => item.field !== field),
      );
    else
      setDialogSearchFilter((prevValue) =>
        prevValue.map((item) =>
          item.field === field ? { field, value, label } : item,
        ),
      );
  };

  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const establishmentsOptions = establishments
    ? establishments.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const debouncedEnterprise = useDebouncedCallback((value) => {
    setEnterpriseInputValue(value);
  }, 500);
  const debouncedEstablishment = useDebouncedCallback((value) => {
    setEstablishmentInputValue(value);
  }, 500);

  const getDefaultValue = (
    field: FilterCriteriaFields,
  ): ISearchFilter | undefined => {
    return dialogSearchFilter.find((item) => item.field === field);
  };

  return (
    <Stack gap={2} p={2}>
      <JSelect
        value={getDefaultValue(FilterCriteriaFields.USER_STATUS)?.value}
        fullWidth
        options={[
          { label: 'Tous', value: '' },
          { label: 'Active', value: 'true' },
          { label: 'Inactive', value: 'false' },
        ]}
        size="small"
        label={t('STATUS')}
        onChange={(event) => {
          appendSearchFilter(
            FilterCriteriaFields.USER_STATUS,
            event.target.value as string,
          );
        }}
      />
      <JSelect
        value={getDefaultValue(FilterCriteriaFields.USER_ROLE)?.value}
        fullWidth
        options={Object.values(UserRole).map((item) => ({
          label: t(item.toUpperCase()),
          value: item,
        }))}
        size="small"
        label={t('USER_TYPE')}
        onChange={(event) => {
          appendSearchFilter(
            FilterCriteriaFields.USER_ROLE,
            event.target.value as string,
          );
        }}
      />

      <JAutocomplete
        {...(!establishmentInputValue
          ? {
              defaultValue: {
                label:
                  getDefaultValue(FilterCriteriaFields.ESTABLISHMENT_ID)
                    ?.label ?? '',
                value:
                  getDefaultValue(FilterCriteriaFields.ESTABLISHMENT_ID)
                    ?.value ?? '',
              },
            }
          : {})}
        fullWidth
        label={t('ESTABLISHMENT')}
        options={establishmentsOptions}
        onChange={(ev, newValue) => {
          appendSearchFilter(
            FilterCriteriaFields.ESTABLISHMENT_ID,
            (newValue as IOption)?.value || '',
            (newValue as IOption)?.label || '',
          );
        }}
        onTextChange={(val) => debouncedEstablishment(val)}
      />

      <JAutocomplete
        {...(!establishmentInputValue
          ? {
              defaultValue: {
                label:
                  getDefaultValue(FilterCriteriaFields.ENTERPRISE_ID)?.label ??
                  '',
                value:
                  getDefaultValue(FilterCriteriaFields.ENTERPRISE_ID)?.value ??
                  '',
              },
            }
          : {})}
        fullWidth
        label={t('ENTERPRISE_NAME')}
        options={enterprisesOptions}
        onChange={(ev, newValue) =>
          appendSearchFilter(
            FilterCriteriaFields.ENTERPRISE_ID,
            (newValue as IOption)?.value || '',
            (newValue as IOption)?.label || '',
          )
        }
        onTextChange={(val) => debouncedEnterprise(val)}
      />

      <JTextFieldBase
        defaultValue={
          filters.find(
            (item) => item.field === FilterCriteriaFields.ESTABLISHMENT_SIRET,
          )?.value
        }
        fullWidth
        label={t('SIRET') ?? undefined}
        onChange={(event) =>
          appendSearchFilter(
            FilterCriteriaFields.ESTABLISHMENT_SIRET,
            event.target.value,
          )
        }
      />

      <JTextFieldBase
        defaultValue={
          filters.find(
            (item) => item.field === FilterCriteriaFields.ENTERPRISE_SIREN,
          )?.value
        }
        fullWidth
        label={t('SIREN') ?? undefined}
        onChange={(event) =>
          appendSearchFilter(
            FilterCriteriaFields.ENTERPRISE_SIREN,
            event.target.value,
          )
        }
      />

      <JTextFieldBase
        defaultValue={
          filters.find(
            (item) => item.field === FilterCriteriaFields.USER_FULLNAME,
          )?.value
        }
        fullWidth
        label={t('FULL_NAME') ?? undefined}
        onChange={(event) =>
          appendSearchFilter(
            FilterCriteriaFields.USER_FULLNAME,
            event.target.value,
          )
        }
      />

      <JTextFieldBase
        defaultValue={
          filters.find((item) => item.field === FilterCriteriaFields.USER_EMAIL)
            ?.value
        }
        fullWidth
        label={t('EMAIL') ?? undefined}
        onChange={(event) =>
          appendSearchFilter(
            FilterCriteriaFields.USER_EMAIL,
            event.target.value,
          )
        }
      />
      <JPhoneInput
        specialLabel={t('USER_PHONE') ?? ''}
        value={getDefaultValue(FilterCriteriaFields.USER_MOBILEPHONE)?.value}
        onChange={(value) =>
          appendSearchFilter(FilterCriteriaFields.USER_MOBILEPHONE, value)
        }
      />
      <Stack alignItems={'flex-end'}>
        <JButton
          data-qa="apply"
          variant="contained"
          color="secondary"
          onClick={() => {
            setFilter(dialogSearchFilter);
          }}
        >
          {t('APPLY')}
        </JButton>
      </Stack>
    </Stack>
  );
};

export default FilterUsersDialogBody;
